import { parseISO } from 'date-fns';
import { BLOG_MANIFEST } from './manifest';
import type { BlogLocale, BlogPost } from './types';

export function getPublishedBlogPostsForLocale(locale: BlogLocale): BlogPost[] {
  return BLOG_MANIFEST.filter(
    (p) => p.language === locale && Boolean(p.slug) && Boolean(p.title),
  );
}

/** Più recenti prima (per featured + lista). */
export function getSortedPublishedBlogPostsForLocale(
  locale: BlogLocale,
): BlogPost[] {
  return [...getPublishedBlogPostsForLocale(locale)].sort(
    (a, b) =>
      parseISO(b.publishedAt).getTime() - parseISO(a.publishedAt).getTime(),
  );
}

/** Normalizza slug da route (es. `articolo.md` → `articolo` per lookup manifest). */
export function normalizeBlogSlug(
  slug: string | undefined,
): string | undefined {
  if (!slug) return undefined;
  return slug.replace(/\.md$/i, '');
}

export function getBlogPostBySlugAndLocale(
  slug: string | undefined,
  locale: BlogLocale,
): BlogPost | undefined {
  const base = normalizeBlogSlug(slug);
  if (!base) return undefined;
  return BLOG_MANIFEST.find((p) => p.slug === base && p.language === locale);
}

export function getAlternateBlogPost(
  post: BlogPost,
  targetLocale: BlogLocale,
): BlogPost | undefined {
  if (!post.translationKey) return undefined;
  return BLOG_MANIFEST.find(
    (candidate) =>
      candidate.translationKey === post.translationKey &&
      candidate.language === targetLocale,
  );
}

export type BlogHubListing =
  | { mode: 'primary'; posts: BlogPost[] }
  | { mode: 'fallback'; posts: BlogPost[]; postLanguage: BlogLocale };

/**
 * Post ordinati per hub blog: se non ci sono articoli nella lingua della pagina,
 * mostra l’altra lingua (es. solo EN in manifest → hub IT non resta vuoto).
 */
export function getSortedPostsForBlogHub(locale: BlogLocale): BlogHubListing {
  const own = getSortedPublishedBlogPostsForLocale(locale);
  if (own.length > 0) {
    return { mode: 'primary', posts: own };
  }
  if (locale === 'it') {
    const en = getSortedPublishedBlogPostsForLocale('en');
    if (en.length > 0) {
      return { mode: 'fallback', posts: en, postLanguage: 'en' };
    }
  }
  if (locale === 'en') {
    const itPosts = getSortedPublishedBlogPostsForLocale('it');
    if (itPosts.length > 0) {
      return { mode: 'fallback', posts: itPosts, postLanguage: 'it' };
    }
  }
  return { mode: 'primary', posts: [] };
}
